import React from "react";
import { Briefcase, Cpu, Leaf, Factory, Rocket, ArrowUpRight } from "lucide-react";

const SERVICES = [
  {
    id: "consulting",
    icon: Briefcase,
    title: "Strategic Business Consulting",
    intro:
      "From market entry to growth strategy — clarity that turns opportunity into a decision.",
    items: [
      "Market entry strategy",
      "Feasibility studies",
      "Business model development",
      "Investor presentations",
      "Growth strategy",
      "Competitive analysis",
    ],
    span: "md:col-span-7",
    dark: true,
  },
  {
    id: "tech",
    icon: Cpu,
    title: "Technology Integration",
    intro: "Bringing global technology to Saudi ground — integrated, deployed, supported.",
    items: [
      "Partnering with global technology providers",
      "Local deployment and integration",
      "Energy, automation, IoT and digital solutions",
      "Project coordination and implementation",
      "Lifecycle service models",
    ],
    span: "md:col-span-5",
    dark: false,
  },
  {
    id: "energy",
    icon: Leaf,
    title: "Energy & Sustainability Solutions",
    intro: "Solar, storage, ESG and optimization — decarbonize with commercial upside.",
    items: [
      "Renewable energy opportunity assessment",
      "Solar, BESS and energy efficiency advisory",
      "Sustainability and ESG solution development",
      "Circular economy concepts",
      "Energy optimization strategies",
    ],
    span: "md:col-span-5",
    dark: false,
  },
  {
    id: "localization",
    icon: Factory,
    title: "Industrial Localization & Manufacturing Strategy",
    intro: "Build Saudi. Roadmaps for local content, OEM partnerships and factory setup.",
    items: [
      "Localization roadmap",
      "Contract manufacturing strategy",
      "Joint venture and OEM partnership models",
      "Local content planning",
      "Factory setup strategy",
    ],
    span: "md:col-span-7",
    dark: true,
  },
  {
    id: "startups",
    icon: Rocket,
    title: "Startup & Innovation Advisory",
    intro: "From product-market fit to investor readiness — velocity for founders.",
    items: [
      "Product-market fit",
      "Technology commercialization",
      "Innovation roadmap",
      "Partnership strategy",
      "Funding and investor readiness",
    ],
    span: "md:col-span-12",
    dark: false,
    wide: true,
  },
];

function ServiceCard({ svc, index }) {
  const Icon = svc.icon;
  const base = svc.dark
    ? "bg-[#0A1128] text-white border-[#0A1128]"
    : "bg-white text-[#0A1128] border-slate-200";
  return (
    <div
      data-testid={`service-card-${svc.id}`}
      className={`col-span-1 ${svc.span} card-lift rounded-2xl border ${base} p-8 md:p-10 flex flex-col relative overflow-hidden`}
    >
      {svc.dark && (
        <div className="absolute inset-0 bg-grid-navy opacity-40 pointer-events-none" />
      )}
      <div className="relative flex items-start justify-between gap-4">
        <div
          className={`w-11 h-11 rounded-xl flex items-center justify-center ${
            svc.dark ? "bg-[#00D084]/15 text-[#00D084]" : "bg-[#059669]/10 text-[#059669]"
          }`}
        >
          <Icon className="w-5 h-5" />
        </div>
        <div
          className={`text-xs font-semibold tracking-widest ${
            svc.dark ? "text-white/40" : "text-slate-400"
          }`}
        >
          0{index + 1}
        </div>
      </div>

      <h3
        className={`relative mt-8 font-display text-2xl md:text-3xl font-medium leading-tight tracking-tight ${
          svc.dark ? "text-white" : "text-[#0A1128]"
        }`}
      >
        {svc.title}
      </h3>
      <p
        className={`relative mt-4 text-[15px] leading-relaxed ${
          svc.dark ? "text-white/70" : "text-slate-600"
        } max-w-xl`}
      >
        {svc.intro}
      </p>

      <ul
        className={`relative mt-6 grid ${
          svc.wide ? "grid-cols-1 md:grid-cols-3" : "grid-cols-1"
        } gap-x-6 gap-y-2 text-sm`}
      >
        {svc.items.map((it, i) => (
          <li
            key={i}
            className={`flex items-start gap-2 ${
              svc.dark ? "text-white/85" : "text-slate-700"
            }`}
          >
            <span
              className={`mt-2 h-1.5 w-1.5 rounded-full flex-shrink-0 ${
                svc.dark ? "bg-[#00D084]" : "bg-[#059669]"
              }`}
            />
            {it}
          </li>
        ))}
      </ul>

      <div className="relative mt-auto pt-8 flex items-center gap-2">
        <span
          className={`text-xs uppercase tracking-widest font-semibold ${
            svc.dark ? "text-[#00D084]" : "text-[#059669]"
          }`}
        >
          Practice area
        </span>
        <ArrowUpRight
          className={`w-4 h-4 ${svc.dark ? "text-[#00D084]" : "text-[#059669]"}`}
        />
      </div>
    </div>
  );
}

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-[#F4F5F7] py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl mb-14 md:mb-20">
          <div className="overline mb-5">What we do</div>
          <h2 className="font-display text-4xl md:text-5xl font-medium text-[#0A1128] tracking-tight leading-[1.05]">
            Five practices, one operating system for growth.
          </h2>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl">
            Purpose-built for founders, investors, manufacturers and government-linked programs
            navigating Saudi Arabia and emerging markets.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {SERVICES.map((s, i) => (
            <ServiceCard svc={s} index={i} key={s.id} />
          ))}
        </div>
      </div>
    </section>
  );
}
